import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DEV_FUNCTION_CODE_MENUS,
  DevFunctionCodeMenu,
} from '@app/shared/constants/dev-function-codes';
import { DevFunctionCodeService } from '@app/shared/services/dev-function-code.service';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzSwitchModule } from 'ng-zorro-antd/switch';

/**
 * Dev-only control for choosing which function codes the current user holds.
 * Renders nothing in a production build.
 */
@Component({
  selector: 'app-dev-function-code-picker',
  standalone: true,
  imports: [
    FormsModule,
    NzButtonModule,
    NzInputModule,
    NzPopoverModule,
    NzSwitchModule,
  ],
  templateUrl: './dev-function-code-picker.component.html',
  styleUrl: './dev-function-code-picker.component.scss',
})
export class DevFunctionCodePickerComponent {
  readonly service = inject(DevFunctionCodeService);
  readonly totalCount = DEV_FUNCTION_CODE_MENUS.reduce(
    (count, menu) => count + 1 + menu.items.length,
    0
  );

  open = false;
  draftEnabled = signal(this.service.enabled());
  draftCodes = signal<ReadonlySet<string>>(new Set(this.service.codes()));
  search = signal('');

  label = computed(() =>
    this.service.enabled()
      ? `Function codes: ${this.service.codes().length}`
      : 'Function codes: off'
  );

  /** Menus matching the search; a matching menu keeps all its codes. */
  visibleMenus = computed<DevFunctionCodeMenu[]>(() => {
    const keyword = this.search().trim().toLowerCase();
    if (!keyword) return DEV_FUNCTION_CODE_MENUS;

    const matches = (item: { code: string; name: string }): boolean =>
      item.code.toLowerCase().includes(keyword) ||
      item.name.toLowerCase().includes(keyword);

    return DEV_FUNCTION_CODE_MENUS.flatMap(menu => {
      if (matches(menu)) return [menu];
      const items = menu.items.filter(matches);
      return items.length ? [{ ...menu, items }] : [];
    });
  });

  /** Reopening starts again from what is applied, dropping unapplied edits. */
  onOpenChange(open: boolean): void {
    if (!open) return;
    this.draftEnabled.set(this.service.enabled());
    this.draftCodes.set(new Set(this.service.codes()));
    this.search.set('');
  }

  /** Ticking a code under a menu also ticks that menu, which it needs. */
  toggle(code: string, checked: boolean): void {
    const codes = new Set(this.draftCodes());
    if (checked) {
      codes.add(code);
      const menu = DEV_FUNCTION_CODE_MENUS.find(candidate =>
        candidate.items.some(item => item.code === code)
      );
      if (menu) codes.add(menu.code);
    } else {
      codes.delete(code);
    }
    this.draftCodes.set(codes);
  }

  isGroupSelected(menu: DevFunctionCodeMenu): boolean {
    const codes = this.draftCodes();
    return this._groupCodes(menu).every(code => codes.has(code));
  }

  /** Selects the menu and every code under it, or clears them if all are set. */
  toggleGroup(menu: DevFunctionCodeMenu): void {
    const select = !this.isGroupSelected(menu);
    const codes = new Set(this.draftCodes());
    this._groupCodes(menu).forEach(code =>
      select ? codes.add(code) : codes.delete(code)
    );
    this.draftCodes.set(codes);
  }

  selectAll(): void {
    this.draftCodes.set(
      new Set(DEV_FUNCTION_CODE_MENUS.flatMap(menu => this._groupCodes(menu)))
    );
  }

  clear(): void {
    this.draftCodes.set(new Set());
  }

  /**
   * Loads the dashboard afresh, so every page re-runs its checks against the
   * new codes and the user is not left on a page they can no longer open.
   */
  apply(): void {
    this.service.save({
      enabled: this.draftEnabled(),
      codes: [...this.draftCodes()],
    });
    window.location.assign(new URL('dashboard', document.baseURI).href);
  }

  private _groupCodes(menu: DevFunctionCodeMenu): string[] {
    return [menu.code, ...menu.items.map(item => item.code)];
  }
}
